import { Box, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DashboardMetric } from '../../../types/dashboard';
import { CardShell } from '../atlas/cards/CardShell';
import { MetricRow } from '../atlas/cards/MetricRow';
import {
  aggregateQuarterlyChartToAnnual,
  attachGoalValuesToChartData,
  buildChartYDomain,
  collectGoalValues,
  extractValueKeys,
  formatMetricTitle,
  getAllTimeValue,
  getMetricSeries,
  getPointValue,
  humanizeFieldName,
  latest,
  parseQuarter,
  pctChange,
  previous,
  readPointFieldGoal,
} from './componentProjectMetricUtils';
import { renderChartGoalLines } from '../ChartGoalLines';

/** Cool Mantine palette aligned with other dashboard multi-series charts. */
const CHART_COLOR_KEYS = ['blue', 'teal', 'violet', 'cyan', 'indigo', 'grape'] as const;

type ActiveGoal = {
  grain?: string;
  periodKey?: string;
  targets?: Record<string, { operator?: string; value?: number; min?: number; max?: number }>;
};

type ComponentProjectMetricChartCardProps = {
  metric: DashboardMetric;
  sectionTitle?: string;
};

function formatAnnualGoalSublabel(
  value: number,
  operator?: string
): string {
  if (operator === '>=' || operator === '>') {
    return `Goal ${value}+/year`;
  }
  if (operator === '<=' || operator === '<') {
    return `Goal ≤${value}/year`;
  }
  return `Goal ${value}/year`;
}

function resolveAnnualGoalForYtd(
  metric: DashboardMetric,
  seriesPoint: Record<string, unknown> | null | undefined,
  valueKeys: string[],
  year: number | undefined
): { value: number; operator?: string } | null {
  if (seriesPoint) {
    const fromPoint = readPointFieldGoal(seriesPoint, valueKeys);
    if (fromPoint !== null) {
      const goal = seriesPoint.goal;
      let operator: string | undefined;
      if (goal && typeof goal === 'object') {
        for (const key of valueKeys) {
          const fieldGoal = (goal as Record<string, { operator?: string }>)[key];
          if (fieldGoal?.operator) {
            operator = fieldGoal.operator;
            break;
          }
        }
      }
      return { value: fromPoint, operator };
    }
  }

  if (year === undefined) return null;
  const activeGoals =
    (metric as DashboardMetric & { goals?: { active?: ActiveGoal[] } }).goals?.active ?? [];
  for (const goal of activeGoals) {
    if (goal.grain !== 'annual' || goal.periodKey !== String(year)) continue;
    for (const key of valueKeys) {
      const target = goal.targets?.[key];
      if (target && typeof target.value === 'number' && Number.isFinite(target.value)) {
        return { value: target.value, operator: target.operator };
      }
    }
  }
  return null;
}

export function ComponentProjectMetricChartCard({
  metric,
  sectionTitle,
}: ComponentProjectMetricChartCardProps) {
  const theme = useMantineTheme();
  const chartColors = CHART_COLOR_KEYS.map((key) => theme.colors[key][6]);
  const series = getMetricSeries(metric);
  const valueKeys = extractValueKeys(series);
  const title = formatMetricTitle(metric.metricId, valueKeys);
  const subtitle = sectionTitle
    ? `${sectionTitle} · ${String(latest(series)?.quarter ?? 'No data')}`
    : String(latest(series)?.quarter ?? 'No data');

  const cur = latest(series);
  const prev = previous(series);
  const curValue = cur ? getPointValue(cur, valueKeys) : null;
  const prevValue = prev ? getPointValue(prev, valueKeys) : null;
  const qoq = curValue !== null ? pctChange(curValue, prevValue) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText =
    qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const currentMeta = parseQuarter(String(cur?.quarter ?? ''));
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(String(point.quarter ?? ''));
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + getPointValue(point, valueKeys), 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(String(point.quarter ?? ''));
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + getPointValue(point, valueKeys), 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText =
    yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const annualGoal = resolveAnnualGoalForYtd(metric, cur, valueKeys, currentMeta?.year);
  const ytdSublabel =
    annualGoal !== null ? (
      <>
        {yoyText}
        <br />
        {formatAnnualGoalSublabel(annualGoal.value, annualGoal.operator)}
      </>
    ) : (
      yoyText
    );

  const allTime = getAllTimeValue(metric.allTime, valueKeys);
  const unitsLabel = metric.units && metric.units !== 'count' ? ` ${metric.units}` : '';
  const latestQuarter = String(cur?.quarter ?? '—');

  const seriesWithGoals = series.map((point) => {
    const next = { ...point };
    const meta = parseQuarter(String(point.quarter ?? ''));
    if (!meta) return next;

    const activeGoals =
      (metric as DashboardMetric & { goals?: { active?: ActiveGoal[] } }).goals?.active ?? [];
    const existingGoal =
      next.goal && typeof next.goal === 'object'
        ? { ...(next.goal as Record<string, unknown>) }
        : {};

    for (const goal of activeGoals) {
      const isAnnual = goal.grain === 'annual' && goal.periodKey === String(meta.year);
      const isQuarterly =
        goal.grain === 'quarter' && goal.periodKey === `${meta.year}-Q${meta.quarter}`;
      if (!isAnnual && !isQuarterly) continue;

      for (const [fieldName, target] of Object.entries(goal.targets ?? {})) {
        if (existingGoal[fieldName]) continue;
        if (typeof target.value === 'number' && Number.isFinite(target.value)) {
          existingGoal[fieldName] = {
            operator: target.operator,
            target: target.value,
          };
        }
      }
    }

    if (Object.keys(existingGoal).length > 0) {
      next.goal = existingGoal;
    }
    return next;
  });

  const quarterlyChartData = attachGoalValuesToChartData(
    seriesWithGoals.map((point) => {
      const row: Record<string, string | number> = {
        quarter: String(point.quarter ?? ''),
      };
      for (const key of valueKeys) {
        row[key] = getPointValue({ [key]: point[key] }, [key]);
      }
      return row;
    }),
    seriesWithGoals,
    valueKeys
  );

  const chartData = aggregateQuarterlyChartToAnnual(quarterlyChartData, valueKeys);

  const goalValues = collectGoalValues(chartData, valueKeys);
  const yDomain = buildChartYDomain(
    chartData.flatMap((point) => valueKeys.map((key) => Number(point[key] ?? 0))),
    goalValues
  );
  const hasGoals = goalValues.length > 0;
  const ChartComponent = hasGoals ? ComposedChart : BarChart;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  if (valueKeys.length === 0 || series.length === 0) {
    return (
      <CardShell title={title} subtitle="No data">
        <Text size="sm" color="dimmed">
          No quarterly data available for this metric yet.
        </Text>
      </CardShell>
    );
  }

  return (
    <CardShell title={title} subtitle={subtitle}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: `${title} reported for ${latestQuarter}. Quarter-over-quarter change compares to the prior reported quarter.`,
              value: (
                <Text size="lg" weight={800}>
                  {curValue ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip:
                annualGoal !== null
                  ? `${title} calendar year to date total (sum of quarters in the current calendar year through ${latestQuarter}). Annual goal: ${formatAnnualGoalSublabel(annualGoal.value, annualGoal.operator)}.`
                  : `${title} calendar year to date total (sum of quarters in the current calendar year through ${latestQuarter}).`,
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: ytdSublabel,
            },
            {
              label: 'All-Time',
              labelTooltip: `${title} across all reported quarters (includes periods not shown on the chart).`,
              value: (
                <Text size="lg" weight={800}>
                  {allTime}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Annual Trend
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis
                dataKey="yearLabel"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={yDomain}
              />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  return (
                    <div style={tooltipShellStyle}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                        {label}
                      </div>
                      {payload.map((entry) => (
                        <div
                          key={String(entry.dataKey)}
                          style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}
                        >
                          {humanizeFieldName(String(entry.dataKey))}: {entry.value}
                          {unitsLabel}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              {hasGoals ? renderChartGoalLines(chartData, valueKeys) : null}
              {valueKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={chartColors[index % chartColors.length]}
                  barSize={18}
                  radius={[4, 4, 0, 0]}
                  activeBar={false}
                />
              ))}
            </ChartComponent>
          </ResponsiveContainer>
        </Box>

        {valueKeys.length > 1 ? (
          <Group position="center" spacing="lg" mt={4}>
            {valueKeys.map((key, index) => (
              <Group key={key} spacing={6} noWrap>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: chartColors[index % chartColors.length],
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" color="dimmed">
                  {humanizeFieldName(key)}
                </Text>
              </Group>
            ))}
          </Group>
        ) : null}
      </Stack>
    </CardShell>
  );
}
